import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { BrushButton } from "@/components/ui/brush-button";
import { useLanguage } from "@/contexts/LanguageContext";
import heroBackground from "@/assets/hero-forest-worker.webp";
import ScrollReveal from "@/components/animations/ScrollReveal";
const HeroSection = () => {
  const { t } = useLanguage();
  const [contentOpacity, setContentOpacity] = useState(1);
  useEffect(() => {
    let rafId: number;
    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        const fadeStart = 50;
        const fadeEnd = 300;
        const scrollY = window.scrollY;
        const opacity =
          scrollY <= fadeStart ? 1 : scrollY >= fadeEnd ? 0 : 1 - (scrollY - fadeStart) / (fadeEnd - fadeStart);
        setContentOpacity(opacity);
      });
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);
  return (
    <section className="relative min-h-[150vh]">
      {/* Fixed Background Image */}
      <div className="fixed inset-0 z-0 h-screen">
        <img
          src={heroBackground}
          alt="Mujer trabajando con laptop en el bosque"
          className="w-full h-full object-cover object-[center_bottom] md:object-center"
        />
        {/* Gradient overlay for legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-sand-50/20 via-transparent to-sand-50/30 md:bg-gradient-to-r md:from-sand-50/40 md:via-transparent md:to-transparent" />
      </div>

      {/* Scrollable Text Content */}
      <div
        className="relative z-10 min-h-screen flex items-start md:items-center pt-24 md:pt-0 transition-opacity duration-150"
        style={{
          opacity: contentOpacity,
        }}
      >
        <div className="w-full px-6 lg:px-16 md:px-[36px]">
          <div className="max-w-xl mx-auto text-center mt-60 md:mt-25">
            {/* Headline */}
            <ScrollReveal>
              <h1 className="text-2xl lg:text-h1 font-extrabold leading-tight tracking-tight mb-4 text-center flex flex-col items-center gap-2 sm:text-4xl">
                <span className="text-forest-900 bg-sand-50/85 px-3 py-1 rounded-lg whitespace-nowrap">
                  {t("hero.title.part1")}
                </span>
                <span className="text-sunset-600 bg-sand-50/85 px-3 py-1 rounded-lg whitespace-nowrap">
                  {t("hero.title.accent")}
                </span>
              </h1>
            </ScrollReveal>

            {/* CTA Buttons */}
            <ScrollReveal delay={160}>
              <div className="flex flex-col gap-4 items-center justify-center sm:flex-row mx-4 md:mx-[58px] md:items-start md:justify-center">
                <Link to="/chat">
                  <BrushButton variant="primary" className="whitespace-nowrap">
                    {t("hero.cta.primary")}
                  </BrushButton>
                </Link>
                <Link to="/quiz">
                  <BrushButton variant="secondary" className="whitespace-nowrap">
                    {t("hero.cta.secondary")}
                  </BrushButton>
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div
        className="fixed bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 transition-opacity duration-150"
        style={{
          opacity: contentOpacity,
        }}
      >
        <div className="flex flex-col items-center">
          <ChevronDown className="h-6 w-6 text-white/90 animate-bounce drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]" />
          <ChevronDown
            className="h-6 w-6 text-white/60 -mt-4 animate-bounce drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]"
            style={{
              animationDelay: "0.15s",
            }}
          />
        </div>
      </div>

      {/* Extended spacer for clean image reveal */}
      <div className="h-[50vh]" aria-hidden="true" />
    </section>
  );
};
export default HeroSection;
